import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IPortalConfig, NotificationType } from 'src/app/models';
import { DocumentService, LoggingService, PortalService } from 'src/app/services';
import { TopNotificationService } from 'src/app/shared/components/top-notification/top-notification.service';

@Component({
  selector: 'medpt-document-viewer-ai-insights',
  templateUrl: './document-viewer-ai-insights.component.html',
  styleUrls: ['./document-viewer-ai-insights.component.scss'],
  standalone: false
})
export class DocumentViewerAiInsightsComponent implements OnChanges {
  @Input() documentName: string = '';
  @Input() assetId: number | null = null;
  @Input() isOpen: boolean = false;
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();

  portalInfo: IPortalConfig;
  isLoading = false;
  insightsData: any = null;
  selectedCategory: any = null;

  // TODO: Remove sample data once API is ready
  private readonly SAMPLE_INSIGHTS_DATA = {
    "executiveSummary": "This document details the Phase I/II MPT-V0001 clinical trial evaluating the immunogenicity and safety of two adjuvanted dose levels of PlacebiMax H7+MF59 influenza vaccine compared to an unadjuvanted vaccine in approximately 700 healthy adults. The study employs a randomized, modified double-blind, multicenter design with extensive safety monitoring and immunogenicity assessments over 13 months. Key conclusions include acceptable safety profiles, detailed risk management, and planned descriptive statistical analyses without formal hypothesis testing.",
    "keyTakeaways": [
      "The MPT-V0001 study is a Phase I/II randomized, modified double-blind, multicenter trial in the US evaluating immunogenicity and safety of two adjuvanted dose levels of PlacebiMax H7+MF59 versus an unadjuvanted vaccine in ~700 healthy adults aged 18+ stratified by age groups, with two doses 21 days apart and 13 months follow-up [Page 1]",
      "Primary immunogenicity endpoints include HAI antibody titers and seroconversion rates at multiple time points up to Day 387; safety endpoints include solicited and unsolicited adverse events, medically attended events, AESIs, and SAEs throughout the study [Page 9]",
      "The MF59 adjuvant is an oil-in-water emulsion with a well-established safety profile that enhances immunogenicity and dose-sparing; prior studies showed acceptable safety with more injection site reactions but no vaccine-related SAEs [Page 20]",
      "Identified safety risks include anaphylaxis, Guillain-Barré syndrome, and anxiety-related vasovagal reactions, with mitigation measures such as 30-minute post-vaccination observation and exclusion criteria [Page 21]",
      "The study employs electronic diaries for safety data collection, with paper diaries as backup, and uses a 3:3:1 randomization ratio with blinding maintained except for vaccine preparers [Page 13]",
      "Eligibility excludes immunodeficiency, recent immunosuppressive therapy, hypersensitivity, bleeding disorders, recent vaccination, and certain medical conditions; women of childbearing potential must use effective contraception and have negative pregnancy tests [Page 29]",
      "Participants discontinuing study intervention for pregnancy, SAEs, or other health risks continue safety follow-up without further dosing; participants receiving SARS-CoV-2 or other vaccines are not withdrawn [Page 38]",
      "Immunogenicity assays include hemagglutination inhibition and seroneutralization with quantitative titers and defined limits of quantitation; no clinical efficacy data are collected [Page 41]",
      "Statistical analyses are descriptive with 95% confidence intervals; no hypothesis testing or multiplicity adjustments are planned; safety data monitored continuously with potential to pause recruitment if needed [Page 53]",
      "Adverse events are comprehensively defined and graded; solicited injection site and systemic reactions are recorded daily in eDiaries; contraceptive guidance is detailed to minimize pregnancy risk during the study [Page 66]",
      "The study complies with international ethical standards, data privacy laws including GDPR, and requires IRB/IEC approvals; informed consent includes options regarding incidental findings [Page 55]",
      "Biological samples and participant data are stored long-term with participant consent for future use; data are coded and transferred under safeguards [Page 46]",
      "The sponsor signed the protocol version 3.0 electronically on September 16, 2022 [Page 83]"
    ],
    "potentialRisks": [
      { "risk": "Anaphylactic reactions requiring 30-minute post-vaccination observation [Page 21]", "category": "Safety", "impact": "High", "pages": [21] },
      { "risk": "Guillain-Barré syndrome identified as an adverse event of special interest and mitigated by exclusion criteria and informed consent [Page 21]", "category": "Safety", "impact": "High", "pages": [21] },
      { "risk": "Anxiety-related vasovagal reactions occurring around vaccination or blood sampling requiring observation until resolution [Page 21]", "category": "Safety", "impact": "Medium", "pages": [21] },
      { "risk": "Potential bias due to unblinded vaccine preparers because of vaccine appearance differences [Page 26]", "category": "Operational", "impact": "Medium", "pages": [26] },
      { "risk": "Participant loss to follow-up despite documented reasonable efforts including phone calls and certified letters [Page 40]", "category": "Operational", "impact": "Medium", "pages": [40] },
      { "risk": "No early safety data review planned which may delay detection of unexpected safety signals [Page 28]", "category": "Safety", "impact": "Medium", "pages": [28] },
      { "risk": "Potential impact of concomitant medications affecting immune response or safety requiring detailed recording and reporting [Page 36]", "category": "Compliance", "impact": "Medium", "pages": [36] },
      { "risk": "Regional or national emergencies restricting site access requiring contingency procedures to maintain participant safety and study integrity [Page 78]", "category": "Operational", "impact": "Medium", "pages": [78] }
    ],
    "importantDates": [
      { "date": "Day 1", "description": "Baseline blood sample collection and first vaccination", "pages": [41] },
      { "date": "Day 22", "description": "Second vaccination and immunogenicity assessment timepoint", "pages": [9] },
      { "date": "Day 43", "description": "Immunogenicity assessment timepoint post-second dose", "pages": [9] },
      { "date": "Day 202", "description": "Immunogenicity assessment for antibody persistence", "pages": [9] },
      { "date": "Day 387", "description": "Final immunogenicity assessment for antibody persistence", "pages": [9] },
      { "date": "3 weeks post-second dose", "description": "Unblinding of immunogenicity and safety data for interim analysis", "pages": [13] },
      { "date": "12 months after last vaccination", "description": "Final database lock and end of safety follow-up", "pages": [13] },
      { "date": "September 16, 2022", "description": "Sponsor electronically signed protocol version 3.0", "pages": [83] }
    ],
    "actionItems": [
      "Ensure 30-minute post-vaccination observation to monitor for anaphylaxis and vasovagal reactions [Page 21]",
      "Maintain strict eligibility screening to exclude participants with immunodeficiency, hypersensitivity, or bleeding disorders [Page 29]",
      "Collect safety data primarily via electronic diaries with paper backup and verify entries during visits [Page 44]",
      "Implement contingency procedures for site access restrictions to preserve participant safety and study integrity [Page 78]",
      "Monitor safety data continuously with internal safety review committee empowered to pause recruitment if needed [Page 53]",
      "Follow up participants who discontinue or withdraw to complete safety assessments unless refused [Page 39]",
      "Store biological samples and coded participant data securely with consent for future use and comply with data privacy regulations [Page 46]",
      "Report all serious adverse events to Sponsor within 24 hours and ensure timely follow-up until resolution or stabilization [Page 69]"
    ],
    "documentTopics": [
      "Clinical trial design and methodology for influenza vaccine evaluation",
      "Immunogenicity and safety assessment of adjuvanted and unadjuvanted influenza vaccines",
      "Adverse event definitions, grading, and reporting procedures",
      "Participant eligibility, discontinuation, and follow-up criteria",
      "Statistical analysis plans and data handling procedures",
      "Regulatory compliance, ethical considerations, and data privacy",
      "Risk management and contingency planning for clinical trials"
    ]
  };

  constructor(
    private documentService: DocumentService,
    private portalService: PortalService,
    private loggingService: LoggingService,
    private notificationService: TopNotificationService
  ) {
    this.portalInfo = this.portalService.portalConfiguation.getValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.assetId != null) {
      this.loadInsights();
    }
  }

  private loadInsights(): void {
    this.isLoading = true;
    this.insightsData = null;
    this.selectedCategory = null;

    // TODO: Replace sample data with API call when ready
    this.documentService.getFileAIInsights(<string>this.portalInfo.SponsorName, <number>this.assetId)
      .then((result: any) => {
      
      if(result == null || result.Insights == null){
        this.insightsData = null;
        this.isLoading = false;
        return;
      }
      setTimeout(() => {
        this.insightsData = this.mapInsightsResponse(result.Insights);
        
        if (this.insightsData?.categories?.length > 0) {
          this.selectedCategory = this.insightsData.categories[0];
        }
        this.isLoading = false;
      }, 800);
    })
    .catch((err: any) => {
      this.loggingService.Error(
        `<MedPoint.WebApp.TriPort.Shared.DocumentViewerAiInsightsComponent.loadInsights> Failed with message=${JSON.stringify(err)}`
      );
      this.insightsData = null;
      this.isLoading = false;
    });
    // Uncomment below to use actual API
    // this.documentService.getDocumentAiInsights(<string>this.portalInfo.SponsorName, <number>this.portalInfo.PortalID, <number>this.assetId)
    //   .then((result: any) => {
    //     this.insightsData = this.mapInsightsResponse(result);
    //     if (this.insightsData?.categories?.length > 0) {
    //       this.selectedCategory = this.insightsData.categories[0];
    //     }
    //   })
    //   .catch((err) => {
    //     this.loggingService.Error(
    //       `<MedPoint.WebApp.TriPort.Shared.DocumentViewerAiInsightsComponent.loadInsights> Failed with message=${JSON.stringify(err)}`
    //     );
    //     this.insightsData = null;
    //   })
    //   .finally(() => {
    //     this.isLoading = false;
    //   });
  }

  private mapInsightsResponse(response: any): any {
    const insights = this.normalizeInsightsPayload(response);
    if (!insights) return null;

    const categories: any[] = [];
    if (insights.executiveSummary) {
      categories.push({
        id: 'executive-summary',
        name: 'Executive Summary',
        icon: '📋',
        color: '#3F51B5',
        description: 'High-level overview of this document',
        items: [{
          icon: '📋',
          text: insights.executiveSummary,
          page: null
        }]
      });
    }

    if (insights.keyTakeaways?.length) {
      categories.push({
        id: 'key-takeaways',
        name: 'Key Takeaways',
        icon: '✅',
        color: '#4CAF50',
        description: 'The most important points from this document',
        items: insights.keyTakeaways.map((text: string) => ({
          icon: '💡',
          text: this.stripPageCitation(text),
          page: this.extractPageNumber(text)
        }))
      });
    }

    if (insights.potentialRisks?.length) {
      categories.push({
        id: 'potential-risks',
        name: 'Potential Risks',
        icon: '⚠️',
        color: '#FF9800',
        description: 'Safety, operational, and compliance risks identified',
        items: insights.potentialRisks.map((risk: any) => ({
          icon: risk.impact === 'High' ? '🔴' : risk.impact === 'Medium' ? '🟡' : '🟢',
          text: this.stripPageCitation(risk.risk),
          page: risk.pages?.[0] || this.extractPageNumber(risk.risk)
        }))
      });
    }

    if (insights.actionItems?.length) {
      categories.push({
        id: 'action-items',
        name: 'Action Items',
        icon: '✔️',
        color: '#2196F3',
        description: 'Actions required based on this document',
        items: insights.actionItems.map((text: string) => ({
          icon: '📌',
          text: this.stripPageCitation(text),
          page: this.extractPageNumber(text)
        }))
      });
    }

    if (insights.importantDates?.length) {
      categories.push({
        id: 'important-dates',
        name: 'Important Dates',
        icon: '📅',
        color: '#9C27B0',
        description: 'Key dates and timepoints from this document',
        items: insights.importantDates.map((item: any) => ({
          icon: '🗓️',
          text: `${item.date} — ${item.description}`,
          page: item.pages?.[0] || null
        }))
      });
    }

    if (insights.documentTopics?.length) {
      categories.push({
        id: 'document-topics',
        name: 'Document Topics',
        icon: '📂',
        color: '#607D8B',
        description: 'Primary subject areas covered in this document',
        items: insights.documentTopics.map((topic: string) => ({
          icon: '📄',
          text: topic,
          page: null
        }))
      });
    }

    return { categories };
  }

  private normalizeInsightsPayload(response: any): any {
    const parsedResponse = this.parseJsonIfString(response);
    const rawInsights = parsedResponse?.Insights ?? parsedResponse;
    const parsedInsights = this.parseJsonIfString(rawInsights);
    return parsedInsights;
  }

  private parseJsonIfString(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private extractPageNumber(text: string): number | null {
    const match = text?.match(/\[Page\s+(\d+)\]/i);
    return match ? parseInt(match[1], 10) : null;
  }

  private stripPageCitation(text: string): string {
    return text?.replace(/\s*\[Page\s+\d+\]\s*$/i, '').trim() || '';
  }

  selectCategory(category: any): void {
    this.selectedCategory = category;
  }

  copyAllInsights(): void {
    if (!this.selectedCategory?.items) {
      return;
    }
    const text = this.selectedCategory.items.map((item: any) => item.text).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      this.notificationService.showNotification("Insights copied to clipboard.", NotificationType.Success);
    });
  }

  close(): void {
    this.closeModal.emit();
  }
}
